import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ModalComponent } from '../shared/modal/modal.component';
import { StepUpService } from '../services/step-up.service';
import { DeviceService } from '../services/device.service';

/**
 * Step-up re-authentication modal host (Keep Me Signed In, Phase 3b).
 *
 * Mounted ONCE at the app root so exactly one modal can surface regardless of
 * which route is active. It is driven entirely by `StepUpService.visible$`: the
 * functional `authInterceptor` opens it by calling `StepUpService.require()`
 * when a sensitive request returns `403 { code: STEP_UP_REQUIRED }`.
 *
 * The user re-enters their current password; on success the interceptor retries
 * the original request. A wrong password (`401` from `POST /user/reauth`) is
 * shown inline and the user may retry without the modal closing. Cancelling
 * (footer button, backdrop, ESC, ×) reports failure so the original request
 * propagates as a normal 403.
 */
@Component({
  selector: 'app-step-up-reauth',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  templateUrl: './step-up-reauth.component.html',
  styleUrl: './step-up-reauth.component.scss',
})
export class StepUpReauthComponent implements OnInit, OnDestroy {
  open = false;
  password = '';
  error = '';
  submitting = false;

  // Stable id so the password input's aria-describedby can point at the inline
  // error when it is shown.
  readonly errorId = 'step-up-error';

  private visibleSub?: Subscription;

  constructor(
    private readonly stepUp: StepUpService,
    private readonly deviceService: DeviceService
  ) {}

  ngOnInit(): void {
    this.visibleSub = this.stepUp.visible$.subscribe((visible) => {
      if (visible) {
        // Fresh state on every open — never carry a prior attempt's password
        // or error into a new step-up prompt.
        this.password = '';
        this.error = '';
        this.submitting = false;
      }
      this.open = visible;
    });
  }

  ngOnDestroy(): void {
    this.visibleSub?.unsubscribe();
  }

  // Backdrop / ESC / × are disabled while a re-auth request is in flight so the
  // outcome isn't reported twice.
  get closeOnBackdrop(): boolean {
    return !this.submitting;
  }

  confirm(): void {
    const password = this.password.trim();
    if (!password || this.submitting) {
      return;
    }
    this.submitting = true;
    this.error = '';

    this.deviceService.reauth(password).subscribe({
      next: () => {
        this.submitting = false;
        // Success — the interceptor retries the original request once.
        this.stepUp.complete(true);
      },
      error: (err: HttpErrorResponse) => {
        this.submitting = false;
        this.error =
          err.status === 401
            ? 'Incorrect password. Please try again.'
            : 'Something went wrong. Please try again.';
        // Leave the modal open so the user can retry; do NOT complete the flow.
      },
    });
  }

  cancel(): void {
    if (this.submitting) {
      return;
    }
    this.stepUp.complete(false);
  }
}
